import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonClasses = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Classes & Objects: The Blueprint of Reality</h1>
                <p className="subtitle">From Robot Factories to Memory Management: The ultimate guide to Python OOP.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine you are running a <strong>Robot Factory</strong>.
                    </p>
                    <p>
                        If you want to build 100 robots, you wouldn't build each one manually from scratch, right?
                        You would draw a design on a piece of paper first. This design says:
                        "Every robot has 2 arms, 2 legs, and a laser color."
                    </p>
                    <ul>
                        <li><strong>The Class (The Blueprint):</strong> The piece of paper with the design. You can't fight a battle with a piece of paper, but it tells you how to make robots.</li>
                        <li><strong>The Object (The Robot):</strong> The actual metal robot you build from the design. You can build 1,000 unique robots from ONE blueprint.</li>
                        <li><strong>Attributes (The Stats):</strong> One robot has "Red" lasers, another has "Blue" lasers. They are different, but they came from the same plan.</li>
                        <li><strong>Methods (The Actions):</strong> What the robot CAN DO. e.g., `shoot_laser()`, `walk()`, `explode()`.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Philosophy: Why OOP?</h2>
                <p>
                    Up until now, you may have been writing <strong>Procedural Code</strong>: a list of instructions executed top-to-bottom.
                    This works for simple scripts, but what happens when you are building a video game, a bank system, or a web browser?
                    If you have 10,000 lines of spaghetti code, changing one variable might break 50 unrelated functions.
                </p>
                <p>
                    <strong>Object-Oriented Programming (OOP)</strong> allows us to group data (Variables) and behavior (Functions) into a single unit called an <strong>Object</strong>.
                    It models the software after the real world.
                </p>
                <ProTip><p><strong>Historical Context:</strong> OOP wasn't invented by Python. It traces back to <strong>Simula 67</strong> (1960s) and <strong>Smalltalk</strong> (1970s). Python has been object-oriented since its birth in 1991, but unlike Java, it doesn't <em>force</em> you to use classes. It lets you choose.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>3. Defining a Class</h2>
                <p>
                    In Python, we use the `class` keyword to define that blueprint.
                    Let's use our Robot Factory as the example.
                </p>

                <CodeBlock language="python" code={`class Robot:
    # The "Constructor". This runs automatically when you build a new Robot.
    def __init__(self, name, color):
        self.name = name    # Everyone gets a unique name
        self.color = color  # Everyone gets a unique color
        self.battery = 100  # Everyone starts with 100% battery

    # A "Action" (Method)
    def say_hello(self):
        print(f"I am {self.name}, a {self.color} robot.")

    def shoot(self):
        print("Pew! Pew!")
        self.battery -= 10 # Using actions costs energy!

# Creating Objects (Instantiation)
r1 = Robot("R2D2", "Blue")
r2 = Robot("C3PO", "Gold")

# Using the Objects
r1.say_hello() # "I am R2D2, a Blue robot."
r2.say_hello() # "I am C3PO, a Gold robot."

print(r1.battery) # 100
r1.shoot()        # Pew! Pew!
print(r1.battery) # 90 (Notice R2D2 lost battery, but C3PO is still at 100!)`} />

                <h3>Understanding `self`</h3>
                <p>
                    You see `self` everywhere in Python classes. It confuses everyone at first.
                </p>
                <ProTip><p><strong>The "Selfie" Analogy:</strong> Think of `self` as the robot pointing at itself.</p>
                    <p>
                        When `R2D2` runs `say_hello`, Python needs to know <em>whose</em> name to print. R2D2's or C3PO's?
                        `self.name` explicitly means "MY name" (the name of the specific robot calling the method).
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. Class Attributes vs Instance Attributes</h2>
                <p>
                    Sometimes, you want a variable to be shared by <strong>ALL</strong> robots (like a Hive Mind), not just one.
                </p>

                <CodeBlock language="python" code={`class Robot:
    # specific to the Class (Shared by everyone)
    manufacturer = "CyberDyne Systems" 

    def __init__(self, name):
        # specific to the Instance (Unique to you)
        self.name = name

r1 = Robot("Terminator")
r2 = Robot("Wall-E")

print(r1.manufacturer) # CyberDyne Systems
print(r2.manufacturer) # CyberDyne Systems

# If we change the Class Attribute...
Robot.manufacturer = "Skynet"

# It changes for EVERYONE instantly!
print(r1.manufacturer) # Skynet
print(r2.manufacturer) # Skynet`} />
            </div>

            <div className="content-section">
                <h2>5. Internal Mechanics: `__dict__` vs `__slots__`</h2>
                <p>
                    How does Python actually store `self.name`?
                    By default, every object in Python has a secret dictionary called `__dict__`.
                </p>
                <CodeBlock language="python" code={`print(r1.__dict__)
# Output: {'name': 'Terminator', 'battery': 100}

# You can even hack it directly (but don't!)
r1.__dict__['name'] = "Arnold"
print(r1.name) # Arnold`} />
                <p>
                    This is why Python is so flexible. You can add attributes to objects <em>after</em> they are created!
                    However, dictionaries use a lot of RAM.
                </p>

                <h3>Optimization: `__slots__`</h3>
                <p>
                    If you are creating <strong>millions</strong> of objects (e.g., pixels in an image), the dictionary overhead is massive.
                    You can restrict attributes using `__slots__` to tell Python: "This class ONLY has these specific attributes."
                </p>
                <CodeBlock language="python" code={`class Point:
    __slots__ = ['x', 'y'] # ONLY these variables are allowed!

    def __init__(self, x, y):
        self.x = x
        self.y = y

p = Point(10, 20)
# p.z = 30 #  AttributeError!`} />
            </div>

            <div className="content-section">
                <h2>6. Deep Dive: Memory & Garbage Collection</h2>
                <p>
                    Unlike C++ where you have to manually delete objects, Python has an automated <strong>Garbage Collector (GC)</strong>.
                    It uses a technique called <strong>Reference Counting</strong>.
                </p>
                <ul>
                    <li>Every object tracks how many variables are pointing to it.</li>
                    <li>When the count drops to 0 (e.g., you set `robot = None`), Python immediately destroys the object and frees the RAM.</li>
                    <li>Python also uses a **Cyclic Garbage Collector** to catch complex cases where two objects point to each other (Circular Reference).</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>7. Real World Example: A Bank Account</h2>
                <p>
                    Let's combine everything into a robust, "Gold Standard" class.
                    Notice the use of <strong>Encapsulation</strong> (hiding internal state) and <strong>Type Hinting</strong>.
                </p>

                <CodeBlock language="python" code={`class BankAccount:
    """A secure representation of a user bank account."""

    # Class Attribute (Interest Rate shared by all)
    interest_rate: float = 0.05

    def __init__(self, owner: str, balance: float):
        self.owner = owner
        # Private attribute (convention) - note the underscore
        self._balance = balance 
        self._is_frozen = False

    def deposit(self, amount: float):
        if self._is_frozen:
            print(" Account is frozen!")
            return
        
        if amount &gt; 0:
            self._balance += amount
            print(f"Deposited \${amount}. New Balance: \${self._balance}")
        else:
            print(" Amount must be positive.")

    def withdraw(self, amount: float):
        if self._is_frozen:
            print(" Account is frozen!")
            return

        if 0 < amount <= self._balance:
            self._balance -= amount
            print(f" Withdrew \${amount}. Remaining: \${self._balance}")
        else:
            print(" Insufficient funds.")

    def apply_interest(self):
        """Adds interest based on the shared class rate."""
        interest = self._balance * BankAccount.interest_rate
        self._balance += interest
        print(f" Interest applied: \${interest:.2f}")

# Usage
my_acc = BankAccount("Rohit", 1000)
my_acc.deposit(500)       # 1500
my_acc.withdraw(200)      # 1300
my_acc.apply_interest()   # Adds 5%`} />
            </div>

            <div className="content-section">
                <h2>8. Refactoring: From Dictionary to Class</h2>
                <p>
                    A common beginner mistake is using dictionaries to model complex objects.
                    Let's see why Classes are better.
                </p>

                <h3>The "Bad" Way (Dictionaries)</h3>
                <CodeBlock language="python" code={`student = {
    "name": "Alice",
    "grades": [90, 85, 88]
}

def get_average(student):
    return sum(student["grades"]) / len(student["grades"])
# Risk: Typos! student["grade"] will break everything.`} />

                <h3>The "Good" Way (Class)</h3>
                <CodeBlock language="python" code={`class Student:
    def __init__(self, name, grades):
        self.name = name
        self.grades = grades
    
    def get_average(self):
        return sum(self.grades) / len(self.grades)

s = Student("Alice", [90, 85, 88])
print(s.get_average())
# Benefit: Code Completion, Structure, and Validation!`} />
            </div>

            <div className="content-section">
                <h2>9. Modern Python: Introduction to `dataclasses`</h2>
                <p>
                    Python 3.7 introduced `@dataclass`. If you are writing a class that is primarily just storing data,
                    writing `__init__`, `__repr__`, and `__eq__` manually is a pain.
                </p>

                <CodeBlock language="python" code={`from dataclasses import dataclass

@dataclass
class Product:
    name: str
    price: float
    in_stock: bool = True

# Python automatically generates __init__, __repr__, etc.
p = Product("Laptop", 999.99)
print(p) 
# Output: Product(name='Laptop', price=999.99, in_stock=True)`} />
            </div>

            <div className="content-section">
                <h2>10. Advanced Pattern: The Singleton</h2>
                <p>
                    Sometimes, you only want ONE of something (like the Universe, or a Database Connection).
                    The <strong>Singleton Pattern</strong> ensures a class only has one instance.
                </p>
                <CodeBlock language="python" code={`class HighCommander:
    _instance = None

    def __new__(cls):
        # Even if you try to make a new one, we give you the OLD one.
        if cls._instance is None:
            cls._instance = super(HighCommander, cls).__new__(cls)
            cls._instance.orders = "Attack!"
        return cls._instance

leader1 = HighCommander()
leader2 = HighCommander()

print(leader1 is leader2) # True (They are the SAME object in memory)
leader1.orders = "Retreat!"
print(leader2.orders) # "Retreat!"`} />
            </div>

            <div className="content-section">
                <h2>11. Interview FAQ</h2>
                <div className="accordion">
                    <details>
                        <summary><strong>Q1: What is `__init__` really?</strong></summary>
                        <p>It's the initializer. It's the first thing that runs after the object is created. Think of it as the "Birth Certificate" signing or "Boot Sequence" of the robot.</p>
                    </details>
                    <details>
                        <summary><strong>Q2: Why do I need to pass `self`?</strong></summary>
                        <p>Python does not hide context like Java (`this`). Explicit is better than implicit. It reminds you that this function belongs to a specific object instance.</p>
                    </details>
                    <details>
                        <summary><strong>Q3: Can I add attributes to an object later?</strong></summary>
                        <p>Yes! Python is dynamic. `r1.new_weapon = "Rocket"` works perfectly fine, unless you use `__slots__`.</p>
                    </details>
                    <details>
                        <summary><strong>Q4: usage of `__slots__`?</strong></summary>
                        <p>It restricts the valid attributes of a class to a fixed set, preventing the creation of a `__dict__` for each instance. This saves significant memory.</p>
                    </details>
                    <details>
                        <summary><strong>Q5: Class vs Instance vs Static Method?</strong></summary>
                        <p>Instance uses `self` (modifies object). Class uses `cls` (modifies class/factory). Static uses neither (standalone utility).</p>
                    </details>
                </div>
            </div>

            <div className="content-section">
                <h2>12. Extended Technical Reference</h2>

                <h3>System Design: When to use Classes?</h3>
                <p>
                    A common question for beginners is: "Should I write a Class or just a bunch of Functions?"
                    Python allows both (unlike Java), so the choice is yours.
                </p>
                <ul className="premium-list">
                    <li><strong>Use Functions:</strong> If your logic is simple, stateless, or a pure transformation (Input {"-&gt;"} Output). e.g., `calculate_tax(amount)`.</li>
                    <li><strong>Use Classes:</strong> If you need to manage <strong>State</strong> (Data) that changes over time, or if you have many functions that all operate on the same data. e.g., `UserSession` (keeps track of login status).</li>
                </ul>

                <h3>Python vs Java/C++</h3>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Python</th>
                            <th>Java/C++</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Definition</strong></td>
                            <td>Dynamic (can change at runtime)</td>
                            <td>Static (fixed at compile time)</td>
                        </tr>
                        <tr>
                            <td><strong>Access Control</strong></td>
                            <td>Convention (`_`, `__`)</td>
                            <td>Strict (`private`, `protected`)</td>
                        </tr>
                        <tr>
                            <td><strong>`this` context</strong></td>
                            <td>Explicit `self`</td>
                            <td>Implicit `this`</td>
                        </tr>
                        <tr>
                            <td><strong>Destructors</strong></td>
                            <td>Garbage Collector (`__del__` exists but rarely used)</td>
                            <td>Manual (`delete`) or RAII</td>
                        </tr>
                    </tbody>
                </table>

                <h3>Historical Context: Old-Style vs New-Style Classes</h3>
                <p>
                    In Python 2.x, there were two types of classes.
                    "Old-Style" classes were defined as `class Foo:`. They were buggy and didn't support features like `super()` correctly.
                    "New-Style" classes inherited from `object`: `class Foo(object):`.
                </p>
                <p>
                    In Python 3.x (what we use today), <strong>ALL</strong> classes are "New-Style" automatically. You don't need to inherit from `object` manually anymore, but you might see it in legacy code.
                </p>
            </div>

        </div>
    );
};

export default PythonClasses;


